/**
 * Origin-country support — typed gate for live-rate shipping availability.
 *
 * Source of truth for which `address_from.country` values our single-account
 * Shippo integration can rate, plus the carrier allowlist + currency + the
 * support tier that drives seller-facing UX (per-listing toggle, eligibility
 * gate, Settings banner).
 *
 * Spec: `.tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md` (rationale, empirical
 * probes, design hooks). Last probe: 2026-06-20 — re-probe required before
 * extending this map (§7 of the spec).
 *
 * Constant doubles as authoritative spec (CLAUDE.md "Где живёт знание" — the
 * runtime truth lives next to the code that consumes it; the doc carries
 * rationale + measurement record).
 *
 * Anchor question (type-driven-design §7): adding a fifth tier or a new
 * origin should change exactly one file — this one. Every `switch (tier)`
 * downstream must end with `default: unreachable(x: never)`.
 *
 * @see .tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md
 * @see .tondocs/shippo/PLAN.md §A "Shipping mode selection" row (Option D)
 * @see .tondocs/skills/type-driven-design.md §1, §7 (ADT + left-fix audit)
 */

// ---------------------------------------------------------------------------
// Discriminated tier — drives mode availability + UX copy
// ---------------------------------------------------------------------------

/**
 * Support tier for a given origin country.
 *
 * - `broad` — full Shippo coverage; live_rate default, legacy opt-in.
 * - `limited` — narrow Shippo coverage (one local-post carrier in local
 *   currency); live_rate available with disclosure, legacy opt-in.
 * - `unsupported` — Shippo returns 0 rates for this origin; live_rate UI
 *   disabled, seller must use legacy flat-rate methods.
 */
export type SupportTier = "broad" | "limited" | "unsupported";

/**
 * Per-origin runtime configuration. One entry per supported origin in
 * {@link SHIPPO_ORIGIN_SUPPORT}.
 *
 * - `carriers` — provider-name allowlist applied to `Rate.provider` (Shippo's
 *   string), case-sensitive against the §1.1 spec table.
 * - `destinationGate` — `"global"` US-origin reaches everywhere Shippo reaches;
 *   `"limited"` EU-origin verified on a handful of lanes; `"none"` unsupported.
 * - `customsRequiredForCrossBorder` — toggles `customs_declaration` assembly
 *   on `POST /transactions` (label buy), NOT on rate quote.
 */
export interface OriginConfig {
  tier: SupportTier;
  currency: "USD" | "GBP" | "EUR";
  carriers: ReadonlyArray<string>;
  destinationGate: "global" | "limited" | "none";
  customsRequiredForCrossBorder: boolean;
}

// ---------------------------------------------------------------------------
// The matrix — empirically verified 2026-06-20
// ---------------------------------------------------------------------------

/**
 * Origins with verified Shippo coverage on our account.
 *
 * Keys are uppercase ISO-3166-1 alpha-2. Lookup via {@link getOriginSupport}
 * (case-insensitive).
 *
 * **Intentional absences.** CA, AU, JP and "everything else" are NOT in this
 * map by design — empirically returned 0 rates on 2026-06-20. Adding any of
 * them requires (a) a fresh probe per
 * `.tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md §7`, (b) updating both the
 * constant and the §2 matrix in the doc, (c) shipping.
 */
export const SHIPPO_ORIGIN_SUPPORT: Readonly<Record<string, OriginConfig>> = {
  US: {
    tier: "broad",
    currency: "USD",
    carriers: ["USPS", "DHLExpress"],
    destinationGate: "global",
    customsRequiredForCrossBorder: true,
  },
  GB: {
    tier: "limited",
    currency: "GBP",
    carriers: ["DPDUK", "HermesUK"],
    destinationGate: "limited",
    customsRequiredForCrossBorder: true,
  },
  DE: {
    tier: "limited",
    currency: "EUR",
    carriers: ["DeutschePost", "DPDDE"],
    destinationGate: "limited",
    customsRequiredForCrossBorder: true,
  },
  FR: {
    tier: "limited",
    currency: "EUR",
    carriers: ["Colissimo", "Chronopost"],
    destinationGate: "limited",
    customsRequiredForCrossBorder: true,
  },
  // CA, AU, JP, …: intentionally absent. 0 rates on our account. Adding =
  // re-probe first (§7 of the spec).
};

/**
 * Look up origin config by country code (case-insensitive).
 *
 * Returns `null` for unsupported origins — consumers branch on that to
 * disable live_rate UI / fall through to flat-rate methods.
 */
export function getOriginSupport(country: string): OriginConfig | null {
  if (!country) return null;
  return SHIPPO_ORIGIN_SUPPORT[country.toUpperCase()] ?? null;
}
